import { useCallback, useId, useMemo, useState } from 'react';
import { useEffectOnce } from 'react-use';
import arrowLeft from '~/assets/icons/arrow_left.svg';
import chest from '~/assets/icons/chest.svg';
import { CollectionImage } from '~/components/collections';
import { CurrencyIcon } from '~/components/collections/CurrencyIcon';
import {
	Button,
	ButtonBorderRadius,
	ButtonColors,
	ButtonHeight,
} from '~/components/ui/Button';
import { Text } from '~/components/ui/Text';
import { useWalletIcon } from '~/hooks';
import { rage } from '~/rage';
import { WalletType, walletTypeCurrency } from '~/shared';
import bg from '../../assets/images/kit_open/bg.jpg';
import {
	setShopKitOpen,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { formatTime } from '../../utils';
import { calcOldPrice } from '../CaseItem';
import { ShopKitOpenItem } from '../ShopKitOpenItem';
import {
	StyledBackgroundCollectionImage,
	StyledBackgroundContainer,
	StyledBackgroundImage,
	StyledBorderSvg,
	StyledBottomContainer,
	StyledButtonContent,
	StyledBuyShadow,
	StyledContainer,
	StyledContainsTag,
	StyledDecDots,
	StyledDiscountContainer,
	StyledDiscountTimerContainer,
	StyledItemsContainer,
	StyledKitContent,
	StyledKitName,
	StyledOldPrice,
	StyledPrice,
	StyledPriceContainer,
	StyledShadow,
	StyledTag,
	StyledTagsContainer,
	StyledTimerContainer,
	StyledTopContainer,
} from './ShopKitOpen.styled';

const ShopKitOpen = () => {
	const dispatch = useLayoutDispatch();
	const gradientId = useId();
	const activeBundle = useLayoutSelector(
		(state) => state.mainMenu.activeBundle,
	);
	const [discountTime, setDiscountTime] = useState('');
	const [endTime, setEndTime] = useState('');
	const [loading, setLoading] = useState(false);
	const disabled = false;

	const currencyIcon = useWalletIcon(
		walletTypeCurrency[activeBundle?.walletType || WalletType.Cash],
		true,
	);

	const countTime = useCallback(() => {
		if (activeBundle?.discountTimestampEnd) {
			const { time, unit } = formatTime(activeBundle?.discountTimestampEnd);
			if (!unit) {
				setDiscountTime(time);
			} else {
				setDiscountTime(`${time} ${unit}`);
			}
		}

		if (activeBundle?.enabledTimestampEnd) {
			const { time, unit } = formatTime(activeBundle?.enabledTimestampEnd);
			if (!unit) {
				setEndTime(time);
			} else {
				setEndTime(`${time} ${unit}`);
			}
		}
	}, [activeBundle]);

	const tags = useMemo(() => {
		return activeBundle?.tags?.map((tag) => rage.cases.getCaseTagById(tag));
	}, [activeBundle]);

	const handleBuy = useCallback(async () => {
		if (!activeBundle?.id || loading || disabled) {
			return;
		}
		setLoading(true);
		await rage.cases.buyBundle(activeBundle?.id);
		dispatch(setShopKitOpen(false));
		setLoading(false);
	}, [activeBundle, loading, disabled]);

	useEffectOnce(() => {
		countTime();
		const interval = setInterval(countTime, 1000);
		return () => clearInterval(interval);
	});

	return (
		<StyledContainer>
			<StyledBackgroundContainer>
				{activeBundle?.icon ? (
					<StyledBackgroundCollectionImage>
						<CollectionImage
							collection="kits"
							id={activeBundle.icon}
							size={500}
							objectFit="cover"
						/>
					</StyledBackgroundCollectionImage>
				) : (
					<StyledBackgroundImage src={bg} />
				)}
				<StyledShadow />
				<StyledBorderSvg
					viewBox="0 0 1800 200"
					preserveAspectRatio="none"
					fill="none"
				>
					<rect
						opacity="0.25"
						x="0.5"
						y="0.5"
						width="1799"
						height="199"
						rx="4.5"
						stroke={`url(#${gradientId})`}
					/>
					<defs>
						<linearGradient
							id={gradientId}
							x1="900"
							y1="0"
							x2="900"
							y2="200"
							gradientUnits="userSpaceOnUse"
						>
							<stop stopColor="white" />
							<stop offset="1" stopColor="white" stopOpacity="0" />
						</linearGradient>
					</defs>
				</StyledBorderSvg>
				<StyledDecDots>
					<div className="small" />
					<div className="big" />
					<div className="small" />
				</StyledDecDots>
			</StyledBackgroundContainer>
			<StyledKitName>
				<span style={{ opacity: 0.5, fontSize: 20 }}>НАБОР</span>
				<span style={{ fontSize: 40, marginTop: 5 }}>{activeBundle?.name}</span>
			</StyledKitName>
			<StyledKitContent>
				<StyledTopContainer>
					<StyledContainsTag>
						<Text>Содержимое набора</Text>
					</StyledContainsTag>
					<StyledTagsContainer>
						{discountTime && activeBundle?.discount ? (
							<StyledDiscountTimerContainer>
								<Text>{activeBundle?.discount * 100}%</Text>
								<div className={'divider'} />
								<Text>{discountTime}</Text>
							</StyledDiscountTimerContainer>
						) : (
							<>
								{endTime && (
									<StyledTimerContainer>
										<Text>{endTime}</Text>
									</StyledTimerContainer>
								)}
								{activeBundle?.discount ? (
									<StyledDiscountContainer>
										<Text>{activeBundle?.discount * 100}%</Text>
									</StyledDiscountContainer>
								) : null}
							</>
						)}
						{tags?.map((tag) => (
							<StyledTag
								$bgColorNormal={tag?.bgColorNormal ?? ''}
								$textColorNormal={tag?.textColorNormal ?? ''}
								key={tag?.id}
							>
								<Text>{tag?.text}</Text>
							</StyledTag>
						))}
					</StyledTagsContainer>
				</StyledTopContainer>
				<StyledItemsContainer>
					{activeBundle?.items?.map((item, index) => {
						const donateItemId = item?.donateItemId || '';
						const type = rage.inventoryDonate.getTypeById(donateItemId);
						const imageId =
							rage.cases.getRewardImageId(donateItemId) ?? donateItemId;

						return (
							<ShopKitOpenItem
								key={index}
								type={type}
								name={donateItemId}
								id={imageId}
							/>
						);
					})}
				</StyledItemsContainer>
			</StyledKitContent>
			<StyledBottomContainer>
				<StyledBuyShadow />
				<StyledContainsTag>
					<Text>Стоимость</Text>
				</StyledContainsTag>
				<StyledPriceContainer>
					<StyledPrice>
						<CurrencyIcon icon={currencyIcon} size={30} />
						<Text>{activeBundle?.price?.toLocaleString('ru-RU')}</Text>
					</StyledPrice>
					{activeBundle?.discount && (
						<StyledOldPrice>
							<CurrencyIcon icon={currencyIcon} size={'lg'} />
							<Text>
								{calcOldPrice(
									activeBundle?.price || 0,
									activeBundle?.discount,
								)?.toLocaleString('ru-RU')}
							</Text>
							<div className={'line'} />
						</StyledOldPrice>
					)}
				</StyledPriceContainer>
				<Button
					color={ButtonColors.Buy}
					height={ButtonHeight.Height50}
					style={{ fontWeight: 500, marginTop: 36, width: 240 }}
					disabled={disabled}
					onClick={handleBuy}
				>
					<StyledButtonContent>
						{!disabled && <img src={chest} />}
						<Text>{disabled ? 'Набор недоступен' : 'Купить набор'}</Text>
					</StyledButtonContent>
				</Button>
				<Button
					color={ButtonColors.Default}
					height={ButtonHeight.Height40}
					borderRadius={ButtonBorderRadius.Radius5}
					style={{ marginTop: 40, width: 170 }}
					onClick={() => dispatch(setShopKitOpen(false))}
				>
					<StyledButtonContent style={{ gap: 5 }}>
						<img src={arrowLeft} style={{ width: 14, height: 14 }} />
						<Text>Вернуться назад</Text>
					</StyledButtonContent>
				</Button>
			</StyledBottomContainer>
		</StyledContainer>
	);
};

export { ShopKitOpen };
